import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAdminMode } from '../context/AdminModeContext';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './ui/command';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Building2, 
  Search, 
  Globe, 
  MapPin, 
  Landmark,
  Store,
  ChevronRight,
  Loader2,
  Command as CommandIcon
} from 'lucide-react';
import { cn } from '../lib/utils';

// Level icons and colors for visual hierarchy
const LEVEL_CONFIG = {
  national: { 
    icon: Globe, 
    color: 'bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30',
    label: 'National'
  },
  regional: { 
    icon: Landmark, 
    color: 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30',
    label: 'Regional'
  },
  state: { 
    icon: MapPin, 
    color: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
    label: 'State'
  },
  local: { 
    icon: Building2, 
    color: 'bg-gray-500/20 text-gray-600 dark:text-gray-400 border-gray-500/30',
    label: 'Local'
  },
  franchise: { 
    icon: Store, 
    color: 'bg-orange-500/20 text-orange-600 dark:text-orange-400 border-orange-500/30',
    label: 'Franchise'
  },
  franchisee: { 
    icon: Store, 
    color: 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30',
    label: 'Franchisee'
  },
};

export function OrganizationPicker({ className }) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const {
    selectedOrg,
    organizations,
    loadingOrgs,
    selectOrganization,
  } = useAdminMode();

  // Keyboard shortcut - Cmd/Ctrl + K
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Group organizations by level for better navigation
  const groupedOrganizations = useMemo(() => {
    if (!organizations.length) return {};
    
    const groups = {
      national: [],
      regional: [],
      state: [],
      local: [],
      franchise: [],
      franchisee: [],
    };
    
    organizations.forEach(org => {
      const level = org.level || 'local';
      if (groups[level]) {
        groups[level].push(org);
      } else {
        groups.local.push(org);
      }
    });
    
    // Remove empty groups
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) delete groups[key];
    });
    
    return groups;
  }, [organizations]);

  // Filter organizations based on search
  const filteredGroups = useMemo(() => {
    if (!searchValue.trim()) return groupedOrganizations;
    
    const search = searchValue.toLowerCase();
    const filtered = {};
    
    Object.entries(groupedOrganizations).forEach(([level, orgs]) => {
      const matchingOrgs = orgs.filter(org => 
        org.name.toLowerCase().includes(search) ||
        (org.city && org.city.toLowerCase().includes(search)) ||
        (org.state && org.state.toLowerCase().includes(search)) ||
        (org.org_type && org.org_type.toLowerCase().includes(search))
      );
      if (matchingOrgs.length > 0) {
        filtered[level] = matchingOrgs;
      }
    });
    
    return filtered;
  }, [groupedOrganizations, searchValue]);

  // Total count for display
  const totalOrgs = organizations.length;
  const filteredCount = Object.values(filteredGroups).flat().length;

  const handleSelect = useCallback((orgId) => {
    selectOrganization(orgId);
    setOpen(false);
    setSearchValue('');
  }, [selectOrganization]);

  const LevelIcon = ({ level }) => {
    const config = LEVEL_CONFIG[level] || LEVEL_CONFIG.local;
    const Icon = config.icon;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <>
      {/* Trigger Button */}
      <Button
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className={cn(
          "justify-between bg-background/50 border-border hover:bg-background/80",
          "w-[180px] sm:w-[220px] md:w-[260px] h-8 sm:h-9",
          className
        )}
        onClick={() => setOpen(true)}
        data-testid="org-picker-trigger"
      >
        <div className="flex items-center gap-2 truncate">
          {selectedOrg ? (
            <>
              <Building2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className="truncate text-sm">{selectedOrg.name}</span>
            </>
          ) : (
            <>
              <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="text-muted-foreground text-sm">Search organizations...</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            <span className="text-xs">⌘</span>K
          </kbd>
        </div>
      </Button>

      {/* Command Dialog */}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <Command className="rounded-lg border shadow-md" shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Search organizations by name, city, or state..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              autoFocus
            />
            {searchValue && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {filteredCount} of {totalOrgs}
              </span>
            )}
          </div>
          
          <CommandList className="max-h-[400px]">
            {loadingOrgs ? (
              <div className="flex items-center justify-center p-6">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : Object.keys(filteredGroups).length === 0 ? (
              <CommandEmpty>
                <div className="flex flex-col items-center gap-2 py-6">
                  <Building2 className="w-10 h-10 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No organizations found</p>
                  {searchValue && (
                    <p className="text-xs text-muted-foreground/70">
                      Try a different search term
                    </p>
                  )}
                </div>
              </CommandEmpty>
            ) : (
              Object.entries(filteredGroups).map(([level, orgs], idx) => {
                const config = LEVEL_CONFIG[level] || LEVEL_CONFIG.local;
                
                return (
                  <React.Fragment key={level}>
                    {idx > 0 && <CommandSeparator />}
                    <CommandGroup heading={
                      <div className="flex items-center gap-2 text-xs uppercase tracking-wider">
                        <LevelIcon level={level} />
                        {config.label} Organizations
                        <Badge variant="outline" className="ml-auto text-[10px] px-1.5">
                          {orgs.length}
                        </Badge>
                      </div>
                    }>
                      {orgs.map(org => (
                        <CommandItem
                          key={org.id}
                          value={org.id}
                          onSelect={() => handleSelect(org.id)}
                          className={cn(
                            "flex items-center gap-3 py-3 px-2 cursor-pointer",
                            selectedOrg?.id === org.id && "bg-accent"
                          )}
                        >
                          <div className={cn(
                            "flex items-center justify-center w-8 h-8 rounded-lg",
                            config.color
                          )}>
                            <LevelIcon level={level} />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium truncate">{org.name}</span>
                              {selectedOrg?.id === org.id && (
                                <Badge className="bg-blue-500/20 text-blue-600 dark:text-blue-400 text-[10px]">
                                  Selected
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              {org.city && org.state && (
                                <span>{org.city}, {org.state}</span>
                              )}
                              {org.org_type && (
                                <Badge variant="outline" className="text-[10px] px-1">
                                  {org.org_type}
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </React.Fragment>
                );
              })
            )}
          </CommandList>
          
          {/* Footer with keyboard hints */}
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono text-[10px]">↑↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono text-[10px]">Enter</kbd>
                Select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono text-[10px]">Esc</kbd>
                Close
              </span>
            </div>
            <span>{totalOrgs} organizations</span>
          </div>
        </Command>
      </CommandDialog>
    </>
  );
}

export default OrganizationPicker;
